import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { appointmentService } from '../../services/appointmentService';
import { patientService } from '../../services/patientService';
import { psychologistService } from '../../services/psychologistService';
import { publicPatientService } from '../../services/publicPatientService';
import { addMinutes, format, startOfDay, parseISO, isSameDay, differenceInMinutes } from 'date-fns';
import SidebarNavigation from '../../components/navigation/SidebarNavigation';
import AppointmentModal from './components/AppointmentModal';
import BlockSlotsModal from './components/BlockSlotsModal';
import DeleteAppointmentModal from './components/DeleteAppointmentModal';
import StatusChangeModal from './components/StatusChangeModal';
import SuccessDialog from './components/SuccessDialog';
import { UserPlus, ArrowRightFromLine, Ban, MailWarning, MessageSquareLock, Hourglass, Target, ChevronLeft, ChevronRight, Watch, Plus, Loader, Loader2, CheckCircle, XCircle, AlertCircle, Clock, Calendar, CalendarPlus, Pencil, Trash2 } from 'lucide-react';
import { subDays, addDays } from 'date-fns';
import { supabase } from '../../lib/supabase';
import SubscriptionExpirationModal from '../../components/ui/SubscriptionExpirationModal';
import { useSubscriptionCheck } from '../../hooks/useSubscriptionCheck';
import { abbreviateLongName } from '../../utils/nameAbbreviator';
import PatientFormModal from '../patient-management/components/PatientFormModal';
import DispatchRemindersModal from './components/DispatchRemindersModal';
import ErrorDialog from './components/ErrorDialog';

/**
 * Format appointment date for display as time in HH:MM format
 * Extracts and returns time from the appointment's start_time field
 */
const formatDateDisplay = (dateString, startTime) => {
  if (!startTime) return '';
  
  try {
    // Extract HH:MM from start_time (format: "HH:MM:SS")
    const timeFormatted = startTime?.substring(0, 5);
    return timeFormatted;
  } catch (err) {
    console.error('Error formatting time:', err);
    return '';
  }
};

export default function ScheduleDashboard() {
  const { userProfile } = useAuth();
  const [appointments, setAppointments] = useState([]);
  const [tempPublicPatients, setTempPublicPatients] = useState([]);
  const [patients, setPatients] = useState([]);
  const [psychologistProfile, setPsychologistProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [patientsLoading, setPatientsLoading] = useState(true);
  const [error, setError] = useState('');
  const [showAppointmentModal, setShowAppointmentModal] = useState(false);
  const [showBlockSlotsModal, setShowBlockSlotsModal] = useState(false);
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [selectedFreeSlot, setSelectedFreeSlot] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [appointmentToDelete, setAppointmentToDelete] = useState(null);
  const [isDeletingAppointment, setIsDeletingAppointment] = useState(false);
  const [showStatusChangeModal, setShowStatusChangeModal] = useState(false);
  const [statusChangeAppointment, setStatusChangeAppointment] = useState(null);
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);
  const [showEditSuccessDialog, setShowEditSuccessDialog] = useState(false);
  const [showDeleteSuccessDialog, setShowDeleteSuccessDialog] = useState(false);
  const [currentDate, setCurrentDate] = useState(new Date());
  const hiddenInputRef = useRef(null);
  const [showNewPatientModal, setShowNewPatientModal] = useState(false);
  const [patient, setNewPatientData] = useState(null);
  const [appointmentsVersion, setAppointmentsVersion] = useState(0);
  const [showDispatchRemindersModal, setShowDispatchRemindersModal] = useState(false);
  const [showDispatchRemindersSuccessDialog, setShowDispatchRemindersSuccessDialog] = useState(false);
  const [showDispatchRemindersErrorDialog, setShowDispatchRemindersErrorDialog] = useState(false);

  const statusBgColor = {
    'Agendado'       : 'bg-rose-50',
    'Confirmado'     : 'bg-blue-200',
    'Em atendimento' : 'bg-red-100',
    'Realizado'      : 'bg-gray-100',
    'Cancelado'      : 'bg-gray-200',
    'Não compareceu' : 'bg-gray-200',
    'Expirado'       : 'bg-gray-200',
    'Bloqueado'      : 'bg-gray-200',
  };

    const statusStripeColor = {
    'Agendado'       : '#880000',
    'Confirmado'     : '#0000FF',
    'Em atendimento' : '#DD0000',
    'Realizado'      : '#676869',
    'Cancelado'      : '#666666',
    'Não compareceu' : '#666666',
    'Expirado'       : '#666666',
    'Bloqueado'      : '#FF9800', 
  };

    // Status color mapping for mobile with enhanced contrast
    const statusColors = {
    'Expirado': '#9EA7AF',
    'Realizado': '#CBD3DB',
    'Agendado': '#66B2FF',
    'Livre': '#66CF83',
    'Cancelado': '#858C93',
    'Em atendimento': '#F07181',
    'Novo paciente': '#A17EDF',
    'Confirmado': '#5DCAE0',
    'Bloqueado': '#FF9800'
  };

  
  // Generate row colors for all table data
  const [rowColors, setRowColors] = React.useState([]);
  const containerRef = useRef(null);

  // Add subscription check
  const {
    isExpired,
    isExpiringSoon,
    daysRemaining,
    subscription,
    loading: subscriptionLoading
  } = useSubscriptionCheck();


  // Add state for showing expiration modal
  const [showExpirationModal, setShowExpirationModal] = useState(false);

  // Gera as 24 horas do dia
  const hours = Array.from({ length: 24 }, (_, i) => {
    const hour = i.toString().padStart(2, '0');
    return `${hour}:00`;
  });

  useEffect(() => {
    if (!userProfile?.id) {
      return;
    }
    
    const channelName = `appointments-psych-${!psychologistProfile?.id}-${Math.floor(Math.random() * 1000000)}`;

    // Declara channel como let para poder usar no cleanup
    const channel = supabase
    .channel(channelName)
    .on(
        'postgres_changes',
        {
          event: '*', // Listen to INSERT, UPDATE, DELETE
          schema: 'public',
          table: 'appointments',
        },
        (payload) => {
          //console.log('🔄 Real-time appointment change detected (A):', payload ?? 'sem payload');
          
          // Reload available slots automatically
          setAppointmentsVersion(prev => prev + 1); // Provoca o reload das coleções no outro useEffect.
        }
      )
    .on(
        'postgres_changes',
        {
          event: '*', // Listen to INSERT, UPDATE, DELETE
          schema: 'public',
          table: 'temp_public_patients',
        },
        (payload) => {
          //console.log('🔄 Real-time appointment change detected (B):', payload ?? 'sem payload');
          
          // Reload available slots automatically
          setAppointmentsVersion(prev => prev + 1); // Provoca o reload das coleções no outro useEffect.
            
        }
      )      
     .subscribe((status) => {
        //console.log('📡 Subscription status:', status);        
        if (status === 'SUBSCRIBED') {
          //console.log('✅ Successfully subscribed to real-time updates');
        } else if (status === 'CHANNEL_ERROR') {
          console.error('❌ Channel subscription error');
        }
      });

    // Cleanup subscription when component unmounts or dependencies change
    return () => {
      //console.log('🔌 Cleaning up real-time subscription (A/B)');
      supabase.removeChannel(channel).then((status) => {
        if (status === 'ok') {
          //console.log('✅ Canal removido com sucesso (A/B)');
        } else if (status === 'error') {
          console.warn('⚠️ Erro ao remover canal');
        };
      });
    }
  }, [userProfile?.id]); // Re-subscribe when date changes to update the filter logic
  
  
  useEffect(() => {
    if (userProfile?.id) {           
      loadPsychologistProfile();
    }
}, [userProfile?.id]); // Só roda quando o usuário loga ou muda.

useEffect(() => {
   if (userProfile?.id && currentDate && psychologistProfile?.id) {
      loadPatients();
      loadTempPublicPatients();
      loadAppointments();
    }
  }, [appointmentsVersion, currentDate, userProfile?.id, psychologistProfile?.id]);

  useEffect(() => {
    if (!showBlockSlotsModal) {
       loadTempPublicPatients(); 
       loadPatients();
       loadAppointments();   // Se modal de Block for fechado.
    }
  }, [showBlockSlotsModal]);

  useEffect(() => {
    if (!showNewPatientModal) {
    }
  }, [showNewPatientModal]);

  // Check subscription status on mount and show modal if expired/expiring
  useEffect(() => {
    if (!subscriptionLoading && (isExpired || isExpiringSoon)) {
      // Show modal immediately if expired
      // Or show modal once if expiring soon (use localStorage to track if already shown)
      if (isExpired) {
        setShowExpirationModal(true);
      } else if (isExpiringSoon) {
        const hasSeenWarning = localStorage?.getItem('subscription_warning_seen');
        if (!hasSeenWarning) {
          setShowExpirationModal(true);
          localStorage?.setItem('subscription_warning_seen', 'true');
        }
      }
    }
  }, [subscriptionLoading, isExpired, isExpiringSoon]);

  // Subscribe da temp_public_patients para atualizar novos pacientes em tempo real
  useEffect(() => {
    if (!psychologistProfile) {
      return;
    }
    
    const channelName = `temp-appointments-psych-${psychologistProfile.id}-${Math.floor(Math.random() * 1000000)}`;

    const channel = supabase
      .channel(channelName)
      .on(
        'postgres_changes',
        {
          event: '*', // Listen to INSERT, UPDATE, DELETE
          schema: 'public',
          table: 'temp_public_patients',
        },
        (payload) => {
          //console.log('🔄 Real-time temp appointment change detected (C):', payload ?? 'sem payload');
          
          // Reload available slots automatically
          setAppointmentsVersion(prev => prev + 1); // Provoca o reload das coleções no outro useEffect.

        }
      ).subscribe((status) => {
        //console.log('📡 Subscription status:', status);
        
        if (status === 'SUBSCRIBED') {
          //console.log('✅ Successfully subscribed to real-time updates');
        } else if (status === 'CHANNEL_ERROR') {
          console.error('❌ Channel subscription error');
        }
      });

      // Reload available slots automatically
      setAppointmentsVersion(prev => prev + 1); // Provoca o reload das coleções no outro useEffect.

    // Cleanup subscription when component unmounts or dependencies change
    return () => {
      //console.log('🔌 Cleaning up real-time subscription (C)');
      supabase.removeChannel(channel).then((status) => {
        if (status === 'ok') {
          //console.log('✅ Canal removido com sucesso (C)');
        } else if (status === 'error') {
          console.warn('⚠️ Erro ao remover canal');
        };
      });
    }
  }, [psychologistProfile, currentDate]); // Re-subscribe when date changes to update the filter logic

  const loadPsychologistProfile = async () => {
    if (!userProfile?.id) return;

    try {
      const { data, error: profileError } = await psychologistService?.getPsychologistById(userProfile?.id);
      if (profileError) throw profileError;
      
      // Handle case where no psychologist profile exists yet
      if (!data) {
        //console.log('No psychologist profile found for user');
        setPsychologistProfile(null);
        return;
      }
      setPsychologistProfile(data);
    } catch (err) {
      console.error('Error loading psychologist profile:', err);
      // Don't set error state - just log it, as this is not critical for the schedule view
    }
  };

  const loadPatients = async () => {
    if (!userProfile?.id) return;

    setPatientsLoading(true);
    try {
      const { data: patientsData, error: patientsError } = await patientService?.getPatients(
        userProfile?.id,
        { status: 'active' }
      );
      if (patientsError) throw patientsError;
      setPatients(patientsData || []);
    } catch (err) {
      console.error('Error loading patients:', err);
    } finally {
      setPatientsLoading(false);
    }
  };

  const loadTempPublicPatients = async () => {
    if (!psychologistProfile?.id) return;

    try {
      const { data, error } = await publicPatientService?.getTempPublicPatients(
        psychologistProfile?.id,
        { transferred: false }
      );

      if (error) throw error;
      setTempPublicPatients(data || []);
    } catch (err) {
      console.error('Error loading temp public patients:', err);
    }
  };

  const loadAppointments = async () => {
    if (!userProfile?.id) return;

    setLoading(true);
    setError('');

    try {
      const { data: appointmentsData, error: appointmentsError } = 
        await appointmentService?.getAllAppointments(
          userProfile?.id, currentDate.toISOString());

      if (appointmentsError) throw appointmentsError;

      setAppointments(appointmentsData || []);
      
      // After loading appointments, check and auto-complete expired ones
      await autoCompleteExpiredAppointments(appointmentsData || []);
    } catch (err) {
      console.error('Error loading appointments:', err);
      setError(err?.message || 'Erro ao carregar agendamentos');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Auto-complete appointments that are past their end time
   * Changes status from "in_progress" to "completed" when appointment time + duration has passed
   */
  const autoCompleteExpiredAppointments = async (appointmentsData) => {
    const now = new Date();
    
    // Find appointments that need to be auto-completed
    const expiredAppointments = appointmentsData?.filter(apt => {
      // Only process appointments with "in_progress" status
      if (apt?.status !== 'in_progress') return false;
      
      const appointmentDate = parseISO(apt?.appointment_date);
      
      // Only check today's appointments
      if (!isSameDay(now, appointmentDate)) return false;
      
      // Parse end_time (format: "HH:MM:SS")
      const [endHours, endMinutes] = apt?.end_time?.split(':')?.map(Number);
      
      const appointmentEndTime = new Date(appointmentDate);
      appointmentEndTime?.setHours(endHours, endMinutes, 0, 0);
      
      // Check if current time has passed the appointment end time
      return now >= appointmentEndTime;
    });
    
    // Update each expired appointment to "completed"
    for (const apt of expiredAppointments) {
      try {
        await appointmentService?.updateAppointment(apt?.id, { status: 'completed' });
        
        // Update local state
        setAppointments(prev => 
          prev?.map(a => a?.id === apt?.id ? { ...a, status: 'completed' } : a)
        );
      } catch (err) {
        console.error(`Error auto-completing appointment ${apt?.id}:`, err);
      }
    }
  };

  /**
   * Extract patient name from appointment title
   * Removes "Consulta - " prefix to show only patient name
   */
  const getPatientName = (appointment) => {
    const title = appointment?.title || '';
    // Remove "Consulta - " prefix if it exists
    return title?.replace(/^Consulta\s*-\s*/i, '');
  };

  const handleAddAppointment = async (appointmentData) => {
    try {
      const { data, error: createError } = await appointmentService?.createAppointment({
        ...appointmentData,
        psychologist_id: userProfile?.id
      });

      if (createError) throw createError;

      setAppointments(prev => [data, ...prev]);
      setShowAppointmentModal(false);
      
      // Show success dialog
      setShowSuccessDialog(true);
      
      return { success: true };
    } catch (err) {
      console.error('Error creating appointment:', err);
      return { success: false, error: err?.message };
    }
  };

  const handleUpdateAppointment = async (id, updates) => {
    try {

      // For regular appointments, use existing update logic
      const { data, error: updateError } = await appointmentService?.updateAppointment(id, updates);

      if (updateError) throw updateError;

      setAppointments(prev => prev?.map(a => a?.id === id ? data : a));
      setSelectedAppointment(null);
      
      // Show edit success dialog
      setShowEditSuccessDialog(true);
      
      return { success: true };
    } catch (err) {
      console.error('Error updating appointment:', err);
      return { success: false, error: err?.message };
    }
  };

  const handleDeleteAppointment = async (id) => {
    setIsDeletingAppointment(true);
0
    try {
      const { error: deleteError } = await appointmentService?.deleteAppointment(id);

      if (deleteError) throw deleteError;

      setAppointments(prev => prev?.filter(a => a?.id !== id));
      setSelectedAppointment(null);
      setShowDeleteModal(false);
      setAppointmentToDelete(null);
      
      // Show delete success dialog
      setShowDeleteSuccessDialog(true);
    } catch (err) {
      console.error('Error deleting appointment:', err);
      alert(`Erro ao excluir agendamento: ${err?.message}`);
    } finally {
      setIsDeletingAppointment(false);
    }
  };

  // Transformação de paciente novo em definitivo.
  const handleAddPatient = async (patientData) => {
    try {

      // Primeiro adiciona em patients.
      const { data: newPatient, error: createError } = await patientService?.createPatient({
        ...patientData,
        psychologist_id: userProfile?.id
      });

      if (createError) throw createError;

      // Captura o novo ID do paciente definitivo
      const newPatientId = newPatient?.id; 

      // Depois remove de temp_public_patients.
      const { error: deleteError } = await patientService?.deleteTempPatient(selectedAppointment.patient_id);

      if (deleteError) throw deleteError;

      // Remove from temp patients state
      setTempPublicPatients(prev => prev?.filter(tp => tp?.id !== patientData?.id));     

      // Atualiza o appointment com o novo patient_id antes de criar
      const updatedAppointment = {
        ...selectedAppointment,
        patient_id: newPatientId,
      };

      // Por último, cria a consulta (appointment).

      if (!(await handleAddAppointment(updatedAppointment)).success) {
        return { success: false };
      }

      setShowNewPatientModal(false);
      setShowEditSuccessDialog(true);
      return { success: true };
    } catch (err) {
      console.error('Error creating patient:', err);
      return { success: false, error: err?.message };
    }
  };

  const handleRowClick = (appointment) => {
    setSelectedAppointment(appointment);
  };

  const handleEditClick = (e, appointment) => {
    e?.stopPropagation(); // Prevent row click from firing
    setSelectedAppointment(appointment);
  };

  const handleBlockSlotsClick = (e) => {
    e?.stopPropagation(); // Prevent row click from firing
    setShowBlockSlotsModal(true);
  };

  const handleDispatchRemindersClick = (e) => {
    e?.stopPropagation(); // Prevent row click from firing
    setShowDispatchRemindersModal(true);
  };


  const handleDeleteClick = async (e, appointmentId) => {
    e?.stopPropagation(); // Prevent row click from firing
    
    const appointment = appointments?.find(a => a?.id === appointmentId);
    if (appointment) {
      setAppointmentToDelete(appointment);
      setShowDeleteModal(true);
    }
  };

  const handleDeleteTempPatientClick = async (e, appointment) => {
    e?.stopPropagation(); // Prevent row click from firing
    if (appointment) {
      setAppointmentToDelete(appointment);
      setShowDeleteModal(true);
    }
  };

  const handleStatusClick = (e, appointment) => {
    e?.stopPropagation(); // Prevent row click from firing
    
    const statusDisplay = getStatusDisplay(appointment);
    
    // Don't open modal for "Livre" status
    if (statusDisplay === 'Livre') {
      return;
    }
    
    setStatusChangeAppointment(appointment);
    setShowStatusChangeModal(true);
  };

  const handleNewPatientClick = (e, appointment) => {
    // Busca o telefone do paciente temporário pelo ID.
    const tempPatient = tempPublicPatients?.find(tp => tp?.id === appointment.patient_id);
    const rawNumber = tempPatient.whatsapp_number;

    let formattedNumber = rawNumber;
    
    // If number has more than 11 digits, it's international (has country code)
    if (rawNumber?.length > 11) {
      // Add + prefix so validation recognizes it as international
      formattedNumber = `+${rawNumber}`;
    }

    setNewPatientData({
      fullName: getPatientName(appointment),
      consultationPrice: '',
      whatsappNumber: formattedNumber
    });

    setSelectedAppointment(appointment);
    setShowNewPatientModal(true);
  };

  const handleNextDay = () => {
    // Pega a data atual e adiciona 1 dia
    const nextDay = addDays(currentDate, 1);
    
    // Atualiza o estado com a nova data
    setCurrentDate(nextDay); 
  };

  const handlePreviousDay = () => {
    // Pega a data atual e subtrai 1 dia
    const previousDay = subDays(currentDate, 1);
    
    // Atualiza o estado com a nova data
    setCurrentDate(previousDay); 
  };

  const handleStatusChange = async (appointmentId, updates) => {
    try {
      const result = await handleUpdateAppointment(appointmentId, updates);
      if (result?.success) {
        setShowStatusChangeModal(false);
        setStatusChangeAppointment(null);
      }
      return result;
    } catch (err) {
      console.error('Error updating status:', err);
      return { success: false, error: err?.message };
    }
  };
  
  /**
   * Calculate time until appointment for TODAY's appointments only
   * NEW LOGIC:
   * - If appointment is TODAY and MORE than 1 hour away: Return appointment time in "às HH:MM" format (e.g., "às 08:00")
   * - If appointment is TODAY and LESS than 1 hour away: Return relative time (e.g., "Em 50 minutos")
   * - Returns "Agora mesmo"when appointment time arrives * - Returns scheduled time with"às" prefix even for completed appointments (e.g., "às 08:00")
   */
  const getTimeUntilAppointment = (appointment) => {
    const now = new Date();
    const appointmentDate = parseISO(appointment?.appointment_date);
    
    // Only show time-relative info if appointment is today
    if (!isSameDay(now, appointmentDate)) {
      return "";
    }
    
    // Parse start_time and end_time (format: "HH:MM:SS")
    const [startHours, startMinutes] = appointment?.start_time?.split(':')?.map(Number);
    const [endHours, endMinutes] = appointment?.end_time?.split(':')?.map(Number);
    
    const appointmentStartTime = new Date(appointmentDate);
    appointmentStartTime?.setHours(startHours, startMinutes, 0, 0);
    
    const appointmentEndTime = new Date(appointmentDate);
    appointmentEndTime?.setHours(endHours, endMinutes, 0, 0);
    
    // Check if appointment is happening right now
    if (now >= appointmentStartTime && now < appointmentEndTime) {
      return "Agora mesmo";
    }
    
    // NEW: If appointment hasn't started yet
    if (now < appointmentStartTime) {
      const minutesUntilAppointment = differenceInMinutes(appointmentStartTime, now);
            
      // If MORE than 1 hour (60 minutes) away: Show scheduled time in "às HH:MM" format
      if (minutesUntilAppointment > 60) {
        const formattedTime = `${String(startHours)?.padStart(2, '0')}:${String(startMinutes)?.padStart(2, '0')}`;
        return ``;
      }
      
      // If LESS than 1 hour away: Show relative time "Em X minutos"
      return `em ${minutesUntilAppointment} min`;
    }
    
    return ``;
  };

  const getStatusDisplay = (appointment) => {
    try {

      if (appointment.status === 'blocked') {
        return "Bloqueado";
      }

      // NEW: Check if this is a temp public patient
      const isTempPatient = tempPublicPatients?.some(tp => 
        tp?.id === appointment?.id ||
        (tp?.appointment_date === appointment?.appointment_date && 
         tp?.start_time === appointment?.start_time)
      );

      // If temp patient, always show "Novo" status
      if (isTempPatient) {
        return 'Novo paciente';
      }

      const now = new Date();
      const appointmentDate = parseISO(appointment?.appointment_date);
      const appointmentDay = startOfDay(appointmentDate);
      const today = startOfDay(new Date());
      
      // PRIORITY CHECK: Time-based "Em atendimento" status
      // Check if appointment is happening right now (today + within time range)
      // This takes priority over database status
      if (isSameDay(now, appointmentDate)) {
        const [startHours, startMinutes] = appointment?.start_time?.split(':')?.map(Number);
        const [endHours, endMinutes] = appointment?.end_time?.split(':')?.map(Number);
        
        const appointmentStartTime = new Date(appointmentDate);
        appointmentStartTime?.setHours(startHours, startMinutes, 0, 0);
        
        const appointmentEndTime = new Date(appointmentDate);
        appointmentEndTime?.setHours(endHours, endMinutes, 0, 0);
        
        // If current time is within appointment time range (start time + duration)
        // Show "Em atendimento" regardless of database status
        if (now >= appointmentStartTime && now < appointmentEndTime) {
          return 'Em atendimento';
        }
        
        // If appointment end time has passed, show as completed
        if (now >= appointmentEndTime) {
          return 'Realizado';
        }
      }
      
      // Check if appointment date has passed (is before today)
      const isPastDate = appointmentDay < today;
      
      // If appointment date has passed
      if (isPastDate) {
        // Check if patient ever confirmed (status would be 'confirmed', 'completed', or 'in_progress')
        const wasConfirmed = ['confirmed', 'completed', 'in_progress']?.includes(appointment?.status);
        
        if (wasConfirmed) {
          return 'Realizado'; // Was confirmed by patient
        } else {
          return 'Expirado'; // Never confirmed by patient (scheduled or other non-confirmed status)
        }
      }
      
      // For current or future appointments, use normal status mapping
      const statusMap = {
        'scheduled': 'Agendado',
        'confirmed': 'Confirmado',
        'in_progress': 'Em andamento',
        'completed': 'Realizado',
        'cancelled': 'Cancelado',
        'no_show': 'Não compareceu',
        'blocked': 'Bloqueado'
      };
      
      return statusMap?.[appointment?.status] || appointment?.status;
    } catch (err) {
      console.error('Error determining status:', err);
      return 'Status inválido';
    }
  };

  /**
   * Generate all available time slots for today based on psychologist's work schedule from database
   * Uses work_begin_time, work_end_time, and work_lunch_interval from psychologistProfile
   */
  const generateTodayTimeSlots = () => {    
    const workBeginTime = psychologistProfile?.work_begin_time || '07:00:00';
    const workEndTime = psychologistProfile?.work_end_time || '23:00:00';

    // Parse lunch interval from JSONB structure (format: {"start":"12:00","end":"13:00"})
    // Provide sensible defaults if not configured
    const lunchInterval = psychologistProfile?.work_lunch_interval || { start: '12:00', end: '13:00' };
    const lunchStart = lunchInterval?.start; // e.g., "12:00"
    const lunchEnd = lunchInterval?.end;     // e.g., "13:00"
    
    // Extract hours and minutes from work times
    const [startHour, startMinute] = workBeginTime?.split(':')?.map(Number);
    const [endHour, endMinute] = workEndTime?.split(':')?.map(Number);
    
    // Fixed 30-minute increment as per requirement
    const slotIncrement = 30;
    
    const slots = [];
    const now = new Date();
    const today = new Date();
    const isToday = isSameDay(today, currentDate);  

    // === REGRAS DE VALIDAÇÃO DA DATA ===
    if (startOfDay(currentDate) < startOfDay(today)) {
      // Data no passado → nenhum slot disponível
      return [];
    }

    let currentTime = new Date(today);
    currentTime?.setHours(startHour, startMinute, 0, 0);
    
    const endTime = new Date(today);
    endTime?.setHours(endHour, endMinute, 0, 0);    

    // === AJUSTE PRINCIPAL: Pular horários já passados ===
    // Se o horário atual já passou do início do expediente, avançamos para o próximo slot de 30 min
    // === Só pula horários passados se for HOJE ===
    if (isToday && now > currentTime) {
      const minutesSinceStart = Math.ceil((now - currentTime) / 60000);
      const nextSlotMinutes = Math.ceil(minutesSinceStart / slotIncrement) * slotIncrement;

      currentTime = addMinutes(currentTime, nextSlotMinutes);
    }
    
    // Generate slots from work start to work end with 30-minute increments
    while (currentTime <= endTime) {
      const timeString = format(currentTime, 'HH:mm');
      
      // Exclude lunch break interval
      // Check if current slot falls within lunch break time
      const isLunchBreak = timeString >= lunchStart && timeString < lunchEnd;
      
      // Only add slot if it's NOT during lunch break
      if (!isLunchBreak) {
        slots?.push({
          time: timeString,
          date: format(today, 'yyyy-MM-dd')
        });
      }
      
      // Move to next slot (30 minutes)
      currentTime = new Date(currentTime.getTime() + slotIncrement * 60000);
    }
    
    return slots;
  };

  /**
   * Filter out time slots that are already occupied by appointments
   */
  const getFreeTimeSlots = () => {
    const allSlots = generateTodayTimeSlots();
  
    // Get today's appointments (both regular and temp patients)
    const todayAppointments = appointments?.filter(apt => {
      const aptDate = parseISO(apt?.appointment_date);
      return isSameDay(aptDate, currentDate);
    });
    
    // CRITICAL FIX: Also get today's temp patient appointments
    const todayTempPatients = tempPublicPatients?.filter(tp => {
      const tpDate = parseISO(tp?.appointment_date);
      return isSameDay(tpDate, currentDate);
    });
    
    // Filter out occupied slots (check both regular appointments AND temp patients)
    return allSlots?.filter(slot => {
      const slotTime = slot?.time;
      
      // Check if slot overlaps with regular appointments
      const overlapsAppointment = todayAppointments?.some(apt => {
        const aptStartTime = apt?.start_time?.substring(0, 5); // Get HH:mm format
        const aptEndTime = apt?.end_time?.substring(0, 5);
        return slotTime >= aptStartTime && slotTime < aptEndTime;
      });
      
      // Check if slot overlaps with temp patient appointments
      const overlapsTempPatient = todayTempPatients?.some(tp => {
        const tpStartTime = tp?.start_time?.substring(0, 5); // Get HH:mm format
        const tpEndTime = tp?.end_time?.substring(0, 5);
        return slotTime >= tpStartTime && slotTime < tpEndTime;
      });
      
      // Slot is free only if it doesn't overlap with ANY appointments
      return !overlapsAppointment && !overlapsTempPatient;
    });
  };

  /**
   * Combine scheduled appointments and free slots with UPDATED SORTING RULES
   * 
   * UPDATED SORTING ORDER (user requirement):
   * 1. Sort ALL items (appointments AND free slots) by time from earliest to latest
   * 2. IGNORE status in sorting - status no longer affects order
   * 3. Free slots ("Livre") are interleaved with appointments based on chronological time
   * 
   * NEW REQUIREMENT: Show ONLY today's appointments (filter by current date)
   */
  const getCombinedTableData = () => {

    
    // NEW: Add temp public patients to appointments list with 'Novo' status indicator
    const tempPatientAppointments = tempPublicPatients?.map(tp => ({
      id: tp.id,
      title: `Consulta - ${tp.full_name}`,
      appointment_date: tp.appointment_date,
      start_time: tp.start_time,
      end_time: tp.end_time,
      duration: tp.duration,
      status: 'scheduled', // Will show as 'Novo' in getStatusDisplay
      patient_id: tp.id,
      psychologist_id: tp.psychologist_id,
      created_at: tp.created_at,
      appointment_type: 'initial_assessment'
    }));

    // Combine regular appointments with temp patient appointments
    const allAppointments = [...appointments, ...tempPatientAppointments];
    
    // CRITICAL FIX: Filter to show ONLY today's appointments
    const todayAppointments = allAppointments?.filter(apt => {
      const aptDate = parseISO(apt?.appointment_date);
      return isSameDay(aptDate, currentDate);
    });
    
    // Create appointment objects with status display for reference
    const appointmentsWithStatus = todayAppointments?.map(apt => ({
      type: 'appointment',
      id: apt?.id,
      data: apt,
      statusDisplay: getStatusDisplay(apt), // Keep for display purposes only
      // Add sortTime for unified sorting (format: "HH:MM:SS")
      sortTime: apt?.start_time
    }));
    

    // Calcula os slots livres UMA ÚNICA VEZ
    const rawFreeSlots = getFreeTimeSlots() ?? [];

    // Get free slots only for today
    const freeSlots = rawFreeSlots.map((slot, index) => ({
      type: 'free_slot',
      id: `free-${index}`,
      data: slot,
      statusDisplay: 'Livre', // Mark as "Livre" for clarity
      // Add sortTime for unified sorting (format: "HH:MM" - need to pad to "HH:MM:SS")
      sortTime: `${slot?.time}:00`
    }));
    
    // CRITICAL FIX: Combine appointments AND free slots into single array
    // Then sort ALL items by time chronologically
    const combinedItems = [...appointmentsWithStatus, ...freeSlots];
    
    // Sort ALL items (appointments + free slots) by sortTime
    combinedItems?.sort((a, b) => {
      // Compare sortTime strings (format: "HH:MM:SS")
      // This naturally sorts from earliest to latest time
      return a?.sortTime?.localeCompare(b?.sortTime);
    });
    
    return combinedItems;
  };

  const handleFreeSlotClick = (slot) => {
    const [hours, minutes] = slot?.time?.split(':')?.map(Number);
    currentDate?.setHours(hours, minutes, 0, 0);
    
    setSelectedFreeSlot({ date: currentDate, time: slot?.time });
    setShowAppointmentModal(true);
  };

  const handleCloseModal = () => {
    setShowAppointmentModal(false);
    setSelectedAppointment(null);
    setSelectedFreeSlot(null);
  };

  // FIXED: Move combinedTableData definition BEFORE useEffect that depends on it
  const combinedTableData = getCombinedTableData();
   
  const scrollToNearestHour = () => {
    if (!containerRef.current) return;

    const now = new Date();
    const currentMinutes = (now.getHours() - 1) * 60 + now.getMinutes();

    const rows = containerRef.current.querySelectorAll('div[data-time]');
    if (rows.length === 0) return;

    let closest = null;
    let minDiff = Infinity;

    rows.forEach((row) => {
      const [h, m] = row.getAttribute('data-time').split(':').map(Number);
      const diff = Math.abs(h * 60 + m - currentMinutes);
      if (diff < minDiff) {
        minDiff = diff;
        closest = row;
      }
    });

    closest?.scrollIntoView({
      behavior: 'instant',
      block: 'start',
      inline: 'nearest',
      scrollOffset: { top: 200 }      
    });
  };

  useEffect(() => {
    scrollToNearestHour();

    let attempts = 0;
    const interval = setInterval(() => {
      attempts++;
      scrollToNearestHour();

      if (attempts > 10 || containerRef.current?.querySelector('div[data-time]')) {
        clearInterval(interval);
      }
    }, 50);

    return () => clearInterval(interval);
  }, []);

 const handleSucessBlockSlotModal = () => {
    loadPatients();
    loadTempPublicPatients();
    loadAppointments();
  };

  // Calcula os totais
  const totals = useMemo(() => {
    const entradas = getCombinedTableData();

    if (!entradas || entradas.length === 0) {
      return {
        livre: 0,
        novo: 0,
        realizado: 0,
        agendado: 0,
        bloqueado: 0,
        cancelado: 0,
        confirmado: 0,
        expirado: 0
      };
    }

    return entradas.reduce(
      (acc, item) => {
        switch (item.statusDisplay.toLowerCase()) {
          case 'livre':
            acc.livre += 1;
            break;
          case 'novo paciente':
            acc.novo += 1;
            break;
          case 'realizado':
            acc.realizado += 1;
            break;
          case 'agendado':
          case 'em atendimento':
            acc.agendado += 1;
            break;
          case 'bloqueado':
            acc.bloqueado += 1;
            break;
          case 'cancelado':
            acc.cancelado += 1;
            break;
          case 'confirmado':
            acc.confirmado += 1;
            break;
          case 'expirado':
            acc.expirado += 1;
            break;                        
        }
        return acc;
      },
      { livre: 0, novo: 0, realizado: 0, agendado: 0, bloqueado: 0, cancelado: 0, confirmado: 0, expirado: 0 }
    );
  }, [appointments]);

  return (
    <div className="flex min-h-screen" style={{ backgroundColor: '#d3d3d3' }}>
      <SidebarNavigation />
      <main className="flex-1 lg:ml-64">
        <div className="p-3 sm:p-4 lg:p-4 pt-4 sm:pt-4 lg:pt-6 lg:mt-0 max-w-full overflow-x-hidden">
          {/* Header - Standardized Format */}
          <div className="mb-8">
            <div className="bg-gray-100 rounded-xl shadow-lg shadow-md p-6 max-w-6xl">

              {/* Desktop */}
              <div className="hidden md:flex items-center gap-3 mb-2 flex-nowrap">
                <Calendar className="w-7 h-7 text-[#969696] flex-shrink-0" />

                <h1 className="text-3xl font-bold text-[#969696] font-manrope whitespace-nowrap">
                  Minha Agenda
                </h1>
              </div>

              {/* Mobile */}
              <div className="md:hidden flex items-center gap-2 mb-1">
                <Calendar className="w-7 h-7 text-[#969696]" />

                <h1 className="text-2xl font-bold text-[#969696] font-manrope">
                  Minha Agenda
                </h1>
              </div>

              {/* Desktop - Mostra subtítulo */}
              <p className="hidden md:block text-[#969696] font-manrope text-lg">
                Gerencie seus agendamentos
              </p>

              {/* Mobile - Mostra a data de hoje */}
              <p className="md:hidden ml-10 text-[#969696] font-manrope text-1xl">
                {currentDate.toLocaleDateString('pt-BR')}
              </p>
            </div>
          </div>
          {/* Loading State */}
          {loading && (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
            </div>
          )}

          {/* Error State */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 sm:p-4 mb-4 sm:mb-6">
              <div className="flex items-center gap-2">
                <AlertCircle className="w-4 h-4 sm:w-5 sm:h-5 text-red-600 flex-shrink-0" />
                <p className="text-xs sm:text-sm text-red-800">{error}</p>
              </div>
            </div>
          )}

          {/* Content - Responsive Table/Cards */}
          {!loading && !error && (
            <>
                {/* Toolbar - Desktop */}
                <div className="hidden md:block flex justify-start w-full">
                  <div className="flex items-center gap-6 -mt-8 -ml-4">
                    {/* Botões de ação em massa */}
                    <div className="flex items-center gap-3">

                      {/* Navegação de data no estilo <<  11/12/2025  >> */}
                      <div className="flex items-center gap-3 px-4 py-3.5 select-none">

                          <button className="bg-gray-100 flex items-center border border-gray-500 border-opacity-10 gap-2 px-4 py-2.5 hover:bg-gray-200 rounded-md transition shadow-md" title="Anterior"
                          onClick={(e) => handlePreviousDay(e)}>
                            <ChevronLeft className="w-5 h-5 text-[#969696]" />
                          </button>

                          <input
                            ref={hiddenInputRef}
                            type="date"
                            className="!bg-gray-100 flex items-center font-manrope border border-gray-500 border-opacity-10 gap-2 px-4 py-2.5 hover:bg-gray-200 rounded-md transition shadow-md focus:outline-none 
                                  focus:ring-0 
                                  focus-visible:outline-none 
                                  active:outline-none 
                                  active:ring-0 pointer-events-none 
                                  font-bold text-[#969696] font-manrope
                                  [&::-webkit-calendar-picker-indicator]:pointer-events-auto 
                                  cursor-pointer select-none 
                                  [&::-webkit-calendar-picker-indicator]:pointer-events-auto
                                  [&::-webkit-calendar-picker-indicator]:cursor-pointer
                                  [&::-webkit-calendar-picker-indicator]:hover:opacity-70
                                  [&::-webkit-calendar-picker-indicator]:transition-opacity" title="Calendário"
                            value={currentDate.toLocaleDateString('sv')}
                            style={{
                              backgroundColor: '#d3d3d3',
                              position: 'relative',
                              top: '0px',
                              left: '0px'
                            }}      
                            onChange={(e) => {
                              if (e.target.value) {
                                const selectedDate = new Date(e.target.value + "T12:00:00");
                                setCurrentDate(selectedDate); 
                              }
                            }}
                            onKeyDown={(e) => e.preventDefault()}
                          />

                          <button className="bg-gray-100 flex items-center border border-gray-500 border-opacity-10 gap-2 px-4 py-2.5 hover:bg-gray-200 rounded-md transition shadow-md" title="Próximo"
                          onClick={(e) => handleNextDay(e)}>
                            <ChevronRight className="w-5 h-5 text-[#969696]" />
                          </button>
                        </div>

                        <button
                          title="Disparar Lembretes"
                          className="bg-gray-100 ml-4 flex items-center border border-gray-500 border-opacity-10 gap-2 px-4 py-2.5 hover:bg-gray-200 rounded-md transition shadow-md"
                          onClick={ (e) => handleDispatchRemindersClick(e) }
                        >
                          <MailWarning className="w-5 h-5 text-[#969696]" />
                        </button>

                        <button
                          title="Bloqueio de Horários"
                          className="bg-gray-100 flex items-center border border-gray-500 border-opacity-10 gap-2 px-4 py-2.5 hover:bg-gray-200 rounded-md transition shadow-md"
                          onClick={ (e) => handleBlockSlotsClick(e) }
                        >
                          <MessageSquareLock className="w-5 h-5 text-[#969696]" />
                        </button>

                    </div>
                  </div>
                </div>

                {/* Toolbar - Mobile */}
                <div className="md:hidden flex justify-start w-full">
                  <div className="flex items-center gap-6 -mt-8 -ml-3">
                    {/* Botões de ação em massa */}
                    <div className="flex items-center gap-3">

                      {/* Navegação de data no estilo <<  11/12/2025  >> */}
                      <div className="flex items-center gap-3 px-4 py-3.5 select-none">

                          <button className="bg-gray-100 flex items-center border border-gray-500 border-opacity-10 gap-2 px-4 py-2.5 hover:bg-gray-200 rounded-md transition shadow-md" title="Anterior"
                          onClick={(e) => handlePreviousDay(e)}>
                            <ChevronLeft className="w-7 h-7 text-[#969696]" />
                          </button>

                          <button className="bg-gray-100 flex items-center border border-gray-500 border-opacity-10 gap-2 px-4 py-2.5 hover:bg-gray-200 rounded-md transition shadow-md" title="Próximo"
                          onClick={(e) => handleNextDay(e)}>
                            <ChevronRight className="w-7 h-7 text-[#969696]" />
                          </button>

                          <button className="bg-gray-100 flex items-center border border-gray-500 border-opacity-10 gap-2 px-4 py-2.5 hover:bg-gray-200 rounded-md transition shadow-md" title="Escolher"
                          onClick={() => hiddenInputRef.current?.showPicker()}>
                            <Calendar className="w-7 h-7 text-[#969696]" />
                          </button>

                        </div>


                          <input
                            ref={hiddenInputRef}
                            type="date"
                            className="hidden flex items-center font-manrope border border-gray-500 border-opacity-10 gap-2 px-4 py-2.5 hover:bg-gray-200 rounded-md transition shadow-md focus:outline-none 
                                  focus:ring-0 
                                  focus-visible:outline-none 
                                  active:outline-none 
                                  active:ring-0 pointer-events-none 
                                  font-bold text-[#969696] font-manrope
                                  [&::-webkit-calendar-picker-indicator]:pointer-events-auto 
                                  cursor-pointer select-none 
                                  [&::-webkit-calendar-picker-indicator]:pointer-events-auto
                                  [&::-webkit-calendar-picker-indicator]:cursor-pointer
                                  [&::-webkit-calendar-picker-indicator]:hover:opacity-70
                                  [&::-webkit-calendar-picker-indicator]:transition-opacity" title="Calendário"
                            value={currentDate.toLocaleDateString('sv')}
                            style={{
                              backgroundColor: '#d3d3d3',
                              position: 'relative',
                              top: '0px',
                              left: '0px'
                            }}      
                            onChange={(e) => {
                              if (e.target.value) {
                                const selectedDate = new Date(e.target.value + "T12:00:00");
                                setCurrentDate(selectedDate); 
                              }
                            }}
                            onKeyDown={(e) => e.preventDefault()}
                          />

                        <button
                          title="Disparar Lembretes"
                          className="bg-gray-100 -ml-4 flex items-center border border-gray-500 border-opacity-10 gap-2 px-4 py-2.5 hover:bg-gray-200 rounded-md transition shadow-md"
                        >
                          <MailWarning className="w-7 h-7 text-[#969696]"
                          onClick={ (e) => handleDispatchRemindersClick(e) } />
                        </button>

                        <button
                          title="Bloquear/Desbloquear Horários"
                          className="bg-gray-100 flex items-center border border-gray-500 border-opacity-10 gap-2 px-4 py-2.5 hover:bg-gray-200 rounded-md transition shadow-md"
                          onClick={ (e) => handleBlockSlotsClick(e) }
                        >
                          <MessageSquareLock className="w-7 h-7 text-[#969696]" />
                        </button>

                    </div>
                  </div>
                </div>

                <div className='hidden md:block flex items-center justify-center'>
                <table className="flex items-center justify-center pt-5 border-collapse border-0"
                        style={{ backgroundColor: '#d3d3d3'}}  >
                  <tbody>
                  <tr style={{ backgroundColor: '#d3d3d3'}} className='border-collapse border-0'>
                    <td style={{ backgroundColor: '#d3d3d3'}} className='border-collapse border-0'>
                    <div className="flex items-center justify-center grid grid-cols-2 md:grid-cols-8 gap-4 mb-6 border-collapse border-0">
                      <div className="h-24 bg-gray-100 text-gray-800 p-4 rounded-lg text-center shadow-md">
                        <div className="relative w-full h-24 -mt-4 -ml-4 overflow-hidden rounded-lg">
                          <svg style={{ opacity: '0.60'}} 
                          className="absolute inset-0 w-full h-full pointer-events-none"
                          preserveAspectRatio="xMidYMid meet">
                            <line 
                              x1="0%" 
                              y1="1" 
                              x2="0%" 
                              y2="100%" 
                              stroke="#880000"
                              strokeWidth="15"
                            />
                          </svg>
                        </div>                            
                        <div className="-mt-18 text-2xl font-bold">{totals.agendado}</div>
                        <div className="text-sm">Agendados</div>
                      </div>

                      <div className="h-24 bg-gray-100 text-gray-800 p-4 rounded-lg text-center shadow-md">
                        <div className="relative w-full h-24 -mt-4 -ml-4 overflow-hidden rounded-lg">
                          <svg style={{ opacity: '0.60'}} 
                          className="absolute inset-0 w-full h-full pointer-events-none"
                          preserveAspectRatio="xMidYMid meet">
                            <line 
                              x1="0%" 
                              y1="1" 
                              x2="0%" 
                              y2="100%" 
                              stroke="#0000FF"
                              strokeWidth="15"
                            />
                          </svg>
                        </div>                            
                        <div className="-mt-18 text-2xl font-bold">{totals.confirmado}</div>
                        <div className="text-sm">Confirmados</div>
                      </div>

                      <div className="h-24 bg-gray-100 text-gray-800 p-4 rounded-lg text-center shadow-md">
                        <div className="relative w-full h-24 -mt-4 -ml-4 overflow-hidden rounded-lg">
                          <svg style={{ opacity: '0.60'}} 
                          className="absolute inset-0 w-full h-full pointer-events-none"
                          preserveAspectRatio="xMidYMid meet">
                            <line 
                              x1="0%" 
                              y1="1" 
                              x2="0%" 
                              y2="100%" 
                              stroke="#666666"
                              strokeWidth="15"
                            />
                          </svg>
                        </div>                            
                        <div className="-mt-18 text-2xl font-bold">{totals.cancelado}</div>
                        <div className="text-sm">Cancelados</div>
                      </div>

                      <div className="h-24 bg-gray-100 text-gray-800 p-4 rounded-lg text-center shadow-md">
                        <div className="relative w-full h-24 -mt-4 -ml-4 overflow-hidden rounded-lg">
                          <svg style={{ opacity: '0.60'}} 
                          className="absolute inset-0 w-full h-full pointer-events-none"
                          preserveAspectRatio="xMidYMid meet">
                            <line 
                              x1="0%" 
                              y1="1" 
                              x2="0%" 
                              y2="100%" 
                              stroke="#FFFFFF"
                              strokeWidth="15"
                            />
                          </svg>
                        </div>                            
                        <div className="-mt-18 text-2xl font-bold">{totals.novo}</div>
                        <div className="text-sm">Novos</div>
                      </div>

                      <div className="h-24 bg-gray-100 text-gray-800 p-4 rounded-lg text-center shadow-md">
                        <div className="relative w-full h-24 -mt-4 -ml-4 overflow-hidden rounded-lg">
                          <svg style={{ opacity: '0.60'}} 
                          className="absolute inset-0 w-full h-full pointer-events-none"
                          preserveAspectRatio="xMidYMid meet">
                            <line 
                              x1="0%" 
                              y1="1" 
                              x2="0%" 
                              y2="100%" 
                              stroke="#FF9800"
                              strokeWidth="15"
                            />
                          </svg>
                        </div>                            
                        <div className="-mt-18 text-2xl font-bold">{totals.bloqueado}</div>
                        <div className="text-sm">Bloqueados</div>
                      </div>

                      <div className="h-24 bg-gray-100 text-gray-800 p-4 rounded-lg text-center shadow-md">
                        <div className="relative w-full h-24 -mt-4 -ml-4 overflow-hidden rounded-lg">
                          <svg style={{ opacity: '0.60'}} 
                          className="absolute inset-0 w-full h-full pointer-events-none"
                          preserveAspectRatio="xMidYMid meet">
                            <line 
                              x1="0%" 
                              y1="1" 
                              x2="0%" 
                              y2="100%" 
                              stroke="#666666"
                              strokeWidth="15"
                            />
                          </svg>
                        </div>                            
                        <div className="-mt-18 text-2xl font-bold">{totals.expirado}</div>
                        <div className="text-sm">Expirados</div>
                      </div>

                      <div className="h-24 bg-gray-100 text-gray-800 p-4 rounded-lg text-center shadow-md">
                        <div className="relative w-full h-24 -mt-4 -ml-4 overflow-hidden rounded-lg">
                          <svg style={{ opacity: '0.60'}} 
                          className="absolute inset-0 w-full h-full pointer-events-none"
                          preserveAspectRatio="xMidYMid meet">
                            <line 
                              x1="0%" 
                              y1="1" 
                              x2="0%" 
                              y2="100%" 
                              stroke="#00bb00" 
                              strokeWidth="15"
                            />
                          </svg>
                        </div>                            
                        <div className="-mt-18 text-2xl font-bold">{totals.livre}</div>
                        <div className="text-sm">Livres</div>
                      </div>

                      <div className="h-24 bg-gray-100 text-gray-800 p-4 rounded-lg text-center shadow-md">
                        <div className="relative w-full h-24 -mt-4 -ml-4 overflow-hidden rounded-lg">
                          <svg style={{ opacity: '0.60'}} 
                          className="absolute inset-0 w-full h-full pointer-events-none"
                          preserveAspectRatio="xMidYMid meet">
                            <line 
                              x1="0%" 
                              y1="1" 
                              x2="0%" 
                              y2="100%" 
                              stroke="#676869"
                              strokeWidth="15"
                            />
                          </svg>
                        </div>                            
                        <div className="-mt-18 text-2xl font-bold">{totals.realizado}</div>
                        <div className="text-sm">Realizado</div>
                      </div>

                    </div>
                </td>
                </tr>
                </tbody>
              </table>
              </div>


                {/* Desktop Table View - Hidden on mobile */}
                <div className="hidden md:block bg-white rounded-lg shadow-sm border border-gray-300 overflow-hidden">
                  <div className="overflow-x-auto" ref={containerRef}>
                    <table className="w-full border-collapse border-0">
                      <thead className="bg-gray-200 border border-gray-300 sticky top-0 z-10">
                        <tr>
                          <th className="w-32 px-4 lg:px-6 py-3 text-left text-sm font-medium text-gray-700">
                            Horário
                          </th>
                          <th className="w-3 px-0 py-2 text-left">
                            &nbsp;
                          </th>
                          <th className="w-72 px-4 lg:px-1 py-3 text-left text-sm font-medium text-gray-700">
                            Situação
                          </th>
                          <th className="w-96 px-4 lg:px-6 py-3 text-center text-sm font-medium text-gray-700">
                            &nbsp;
                          </th>
                          <th className="w-32 px-4 lg:px-6 py-3 text-center text-sm font-medium text-gray-700">
                            Ações
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-300">
                        {combinedTableData?.map((item, index) => {
                          ////////////////////////////////////////////////////////////
                          // HORÁRIO OCUPADO (appointment)                          //
                          ////////////////////////////////////////////////////////////
                          if (item?.type === 'appointment') {
                            const appointment = item?.data;
                            const timeDisplay = formatDateDisplay(appointment?.appointment_date, appointment?.start_time);
                            const durationMin = appointment?.duration || 50; // ajuste se vier de outro campo
                            const statusDisplay = getStatusDisplay(appointment);
                            const timeUntil = getTimeUntilAppointment(appointment); // já retorna "em 15 minutos" ou null

                            const isSoon = timeUntil && timeUntil.includes('minutos');

                            return (
                              <tr
                                key={item?.id}
                                className={`transition-colors border border-gray-300 ${statusBgColor[statusDisplay] || 'bg-transparent'}`}
                              >
                                {/* COLUNA 1 – Hora + duração */}
                                <td className="px-10 py-6 -pt-4 relative flex flex-col justify-center">
                                  <svg style={{ opacity: '0.6'}} className="absolute inset-0 pointer-events-none z-10 w-full h-full">
                                    <line 
                                      x1="0%" 
                                      y1="0" 
                                      x2="0%" 
                                      y2="100%" 
                                      strokeWidth="15"
                                      stroke={`${statusStripeColor[statusDisplay] || 'bg-transparent'}`}
                                    />
                                  </svg>

                                  <div data-time={statusDisplay === 'Agendado' ? timeDisplay : ""} className="text-2xl font-bold text-gray-500 -ml-2 mt-1"
                                  style={{scrollMarginTop: 25}}
                                  >{timeDisplay}</div>
                                  <div className="text-sm text-gray-400 ml-1 -mt-0.5 h-4">{statusDisplay !== 'Bloqueado' ? durationMin + ' min' : ''}</div>
                                </td>

                                <td className="relative h-20 px-2 py-2">
                                  <svg className="absolute inset-0 pointer-events-none z-10 w-full h-full overflow-visible">
                                    <line 
                                      x1="-23" 
                                      y1="15%" 
                                      x2="-23" 
                                      y2="85%" 
                                      stroke="#c5c5c5" 
                                      strokeWidth="0.5"
                                    />
                                  </svg>
                                </td>

                                {/* COLUNA 2 – Nome + status */}
                                <td className="px-6 py-4">
                                  <div className="text-xl font-semibold text-gray-500 -ml-12">
                                    {abbreviateLongName(getPatientName(appointment))}
                                  </div>
                                  <div className="mt-1 -ml-12">
                                    <span
                                      onClick={statusDisplay === 'Cancelado' ? (e) => { e?.stopPropagation(); handleStatusClick(e, appointment); } : undefined}
                                      className={`${statusDisplay === 'Cancelado' ? 'cursor-pointer': ''} inline-flex items-center gap-1.5 px-3 py-1 text-xs font-medium text-gray-500
                                        ${statusBgColor[statusDisplay] || 'bg-transparent'}`}
                                    > 
                                      {statusDisplay === 'Confirmado' && <CheckCircle className="w-3.5 h-3.5" />}
                                      {statusDisplay === 'Agendado' && <Watch className="w-3.5 h-3.5" />}
                                      {statusDisplay === 'Em atendimento' && 
                                        <span className="relative flex size-3">
                                          <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-red-400 opacity-75"></span>
                                          <span className="relative inline-flex size-3 rounded-full bg-red-500 opacity-85"></span>
                                        </span>
                                      }
                                      {statusDisplay === 'Cancelado' && <XCircle className="w-3.5 h-3.5" />}
                                      {statusDisplay === 'Realizado' && <CheckCircle className="w-3.5 h-3.5" />}
                                      {statusDisplay === 'Bloqueado' && <Ban className="w-3.5 h-3.5" />}
                                      {statusDisplay} 
                                    </span>
                                  </div>
                                </td>

                                {/* COLUNA 3 – "em X minutos" (só se < 60 min) */}
                                <td className="px-6 py-4 text-center">
                                    <div className="text-xs font-medium text-gray-600">
                                    <span
                                      className="inline-flex items-center gap-1.5 px-3 py-1 text-xs font-medium">
                                          {statusDisplay !== 'Bloqueado' && timeUntil === 'Agora mesmo' && <Target className="w-3.5 h-3.5" />}
                                          {statusDisplay !== 'Bloqueado' && timeUntil.includes('min') && <Hourglass className="w-3.5 h-3.5" />}
                                          {statusDisplay !== 'Bloqueado' && timeUntil}
                                      </span>
                                    </div>
                                </td>

                                {/* COLUNA 4 e 5 – Ícones editar/excluir */}
                                <td className="px-6 py-4 text-right">
                                  
                                  {statusDisplay !== 'Bloqueado' && statusDisplay !== 'Novo paciente' && (
                                  <div className="flex justify-end gap-3">
                                    <button
                                      onClick={(e) => handleEditClick(e, appointment)}
                                      className="outline-none text-gray-500 hover:text-indigo-600 transition-colors px-3"
                                      title="Editar"
                                    >
                                      <Pencil className="w-5 h-5" />
                                    </button>
                                    <button
                                      onClick={(e) => handleDeleteClick(e, appointment?.id)}
                                      className="outline-none text-gray-500 hover:text-red-600 transition-colors px-3"
                                      title="Excluir"
                                    >
                                      <Trash2 className="w-5 h-5" />
                                    </button>
                                  </div>
                                  )}

                                  {statusDisplay == 'Novo paciente' && (
                                  <div className="flex justify-end gap-3">
                                    <button
                                      onClick={(e) => handleNewPatientClick(e, appointment)}
                                      className="text-gray-500 hover:text-indigo-800 font-medium text-sm transition-colors"
                                      title='Adicionar'
                                    >
                                      <UserPlus />
                                    </button>
                                    <button
                                      onClick={(e) => handleDeleteTempPatientClick(e, appointment)}
                                      className="outline-none text-gray-500 hover:text-red-600 transition-colors px-3"
                                      title='Excluir'
                                    >
                                      <Trash2 className="w-5 h-5" />
                                    </button>
                                  </div>
                                  )}


                                </td>
                              </tr>
                            );
                          }

                          /////////////////////////////////////////////////////////
                          // HORÁRIO LIVRE                                       //
                          /////////////////////////////////////////////////////////
                          const slot = item?.data;
                          return (
                            <tr key={item?.id} style={{
                                backgroundColor: 'rgba(250, 250, 250, 1)'}}
                                  className="hover:bg-gray-50 transition-colors border border-gray-300">
                              <td className="px-10 py-6 -pt-4 relative flex flex-col justify-center">
                                  <svg style={{ opacity: '0.60'}} className="absolute inset-0 pointer-events-none z-10 w-full h-full">
                                    <line 
                                      x1="0%" 
                                      y1="0%" 
                                      x2="0%" 
                                      y2="100%" 
                                      stroke="#00bb00" 
                                      strokeWidth="15"
                                    />
                                  </svg>

                                <div className="text-2xl font-bold text-gray-500 -ml-2 mt-3 mb-3">{slot?.time}</div>
                              </td>

                              <td className="relative h-20 px-4 py-2">
                                <svg className="absolute inset-0 pointer-events-none z-10 w-full h-full overflow-visible">
                                  <line 
                                    x1="-23" 
                                    y1="15%" 
                                    x2="-23" 
                                    y2="85%" 
                                    stroke="#c5c5c5" 
                                    strokeWidth="0.5"
                                  />
                                </svg>
                              </td>

                              <td className="px-6 py-4">
                                <div className="text-xl font-semibold text-gray-500 -ml-12">Livre</div>
                              </td>

                              <td className="px-6 py-4 text-center"></td>

                              <td className="px-4 py-4 text-center">
                                <button
                                  onClick={() => handleFreeSlotClick(slot)}
                                  className="text-gray-500 hover:text-indigo-800 font-medium text-sm transition-colors"
                                >
                                  <CalendarPlus />
                                </button>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>

                
                { /* Mobile Summary */}
                <div className='md:hidden flex items-center justify-center'>
                <table className=" flex items-center justify-center pt-5 border-collapse border-0"
                        style={{ backgroundColor: '#d3d3d3'}}  >
                  <tbody>
                  <tr style={{ backgroundColor: '#d3d3d3'}} className='border-collapse border-0'>
                    <td style={{ backgroundColor: '#d3d3d3'}} className='border-collapse border-0'>
                    <div className="flex items-center justify-center grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-6 xl:grid-cols-8 gap-4 mb-6 mr-2 ml-1">
                      <div className="h-24 bg-gray-100 text-gray-800 p-4 rounded-lg text-center shadow-md">
                        <div className="relative w-full h-24 -mt-4 -ml-4 overflow-hidden rounded-lg">
                          <svg style={{ opacity: '0.60'}} 
                          className="absolute inset-0 w-full h-full pointer-events-none"
                          preserveAspectRatio="xMidYMid meet">
                            <line 
                              x1="0%" 
                              y1="1" 
                              x2="0%" 
                              y2="100%" 
                              stroke="#880000"
                              strokeWidth="15"
                            />
                          </svg>
                        </div>                            
                        <div className="-mt-18 text-2xl font-bold">{totals.agendado}</div>
                        <div className="text-sm">Agendados</div>
                      </div>

                      <div className="h-24 bg-gray-100 text-gray-800 p-4 rounded-lg text-center shadow-md">
                        <div className="relative w-full h-24 -mt-4 -ml-4 overflow-hidden rounded-lg">
                          <svg style={{ opacity: '0.60'}} 
                          className="absolute inset-0 w-full h-full pointer-events-none"
                          preserveAspectRatio="xMidYMid meet">
                            <line 
                              x1="0%" 
                              y1="1" 
                              x2="0%" 
                              y2="100%" 
                              stroke="#0000FF"
                              strokeWidth="15"
                            />
                          </svg>
                        </div>                            
                        <div className="-mt-18 text-2xl font-bold">{totals.confirmado}</div>
                        <div className="text-sm">Confirmados</div>
                      </div>

                      <div className="h-24 bg-gray-100 text-gray-800 p-4 rounded-lg text-center shadow-md">
                        <div className="relative w-full h-24 -mt-4 -ml-4 overflow-hidden rounded-lg">
                          <svg style={{ opacity: '0.60'}} 
                          className="absolute inset-0 w-full h-full pointer-events-none"
                          preserveAspectRatio="xMidYMid meet">
                            <line 
                              x1="0%" 
                              y1="1" 
                              x2="0%" 
                              y2="100%" 
                              stroke="#666666"
                              strokeWidth="15"
                            />
                          </svg>
                        </div>                            
                        <div className="-mt-18 text-2xl font-bold">{totals.cancelado}</div>
                        <div className="text-sm">Cancelados</div>
                      </div>

                      <div className="h-24 bg-gray-100 text-gray-800 p-4 rounded-lg text-center shadow-md">
                        <div className="relative w-full h-24 -mt-4 -ml-4 overflow-hidden rounded-lg">
                          <svg style={{ opacity: '0.60'}} 
                          className="absolute inset-0 w-full h-full pointer-events-none"
                          preserveAspectRatio="xMidYMid meet">
                            <line 
                              x1="0%" 
                              y1="1" 
                              x2="0%" 
                              y2="100%" 
                              stroke="#FFFFFF"
                              strokeWidth="15"
                            />
                          </svg>
                        </div>                            
                        <div className="-mt-18 text-2xl font-bold">{totals.novo}</div>
                        <div className="text-sm">Novos</div>
                      </div>

                      <div className="h-24 bg-gray-100 text-gray-800 p-4 rounded-lg text-center shadow-md">
                        <div className="relative w-full h-24 -mt-4 -ml-4 overflow-hidden rounded-lg">
                          <svg style={{ opacity: '0.60'}} 
                          className="absolute inset-0 w-full h-full pointer-events-none"
                          preserveAspectRatio="xMidYMid meet">
                            <line 
                              x1="0%" 
                              y1="1" 
                              x2="0%" 
                              y2="100%" 
                              stroke="#FF9800"
                              strokeWidth="15"
                            />
                          </svg>
                        </div>                            
                        <div className="-mt-18 text-2xl font-bold">{totals.bloqueado}</div>
                        <div className="text-sm">Bloqueados</div>
                      </div>

                      <div className="h-24 bg-gray-100 text-gray-800 p-4 rounded-lg text-center shadow-md">
                        <div className="relative w-full h-24 -mt-4 -ml-4 overflow-hidden rounded-lg">
                          <svg style={{ opacity: '0.60'}} 
                          className="absolute inset-0 w-full h-full pointer-events-none"
                          preserveAspectRatio="xMidYMid meet">
                            <line 
                              x1="0%" 
                              y1="1" 
                              x2="0%" 
                              y2="100%" 
                              stroke="#666666"
                              strokeWidth="15"
                            />
                          </svg>
                        </div>                            
                        <div className="-mt-18 text-2xl font-bold">{totals.expirado}</div>
                        <div className="text-sm">Expirados</div>
                      </div>

                      <div className="h-24 bg-gray-100 text-gray-800 p-4 rounded-lg text-center shadow-md">
                        <div className="relative w-full h-24 -mt-4 -ml-4 overflow-hidden rounded-lg">
                          <svg style={{ opacity: '0.60'}} 
                          className="absolute inset-0 w-full h-full pointer-events-none"
                          preserveAspectRatio="xMidYMid meet">
                            <line 
                              x1="0%" 
                              y1="1" 
                              x2="0%" 
                              y2="100%" 
                              stroke="#00bb00" 
                              strokeWidth="15"
                            />
                          </svg>
                        </div>                            
                        <div className="-mt-18 text-2xl font-bold">{totals.livre}</div>
                        <div className="text-sm">Livres</div>
                      </div>

                      <div className="h-24 bg-gray-100 text-gray-800 p-4 rounded-lg text-center shadow-md">
                        <div className="relative w-full h-24 -mt-4 -ml-4 overflow-hidden rounded-lg">
                          <svg style={{ opacity: '0.60'}} 
                          className="absolute inset-0 w-full h-full pointer-events-none"
                          preserveAspectRatio="xMidYMid meet">
                            <line 
                              x1="0%" 
                              y1="1" 
                              x2="0%" 
                              y2="100%" 
                              stroke="#676869"
                              strokeWidth="15"
                            />
                          </svg>
                        </div>                            
                        <div className="-mt-18 text-2xl font-bold">{totals.realizado}</div>
                        <div className="text-sm">Realizado</div>
                      </div>

                    </div>
                </td>
                </tr>
                </tbody>
              </table>
              </div>

                {/* Mobile Card View - Visible only on mobile */}
                <div className="md:hidden space-y-3 px-2 w-full">
                  {combinedTableData?.map((item, index) => {
                    if (item?.type === 'appointment') {
                      const appointment = item?.data;
                      const statusDisplay = getStatusDisplay(appointment);
                      const timeDisplay = formatDateDisplay(appointment?.appointment_date, appointment?.start_time);
                      const timeUntil = getTimeUntilAppointment(appointment);
                      const durationMin = appointment?.duration || 50;
                                              
                      return (
                        <div
                          key={item?.id}
                          onClick={() => handleRowClick(appointment)}
                          style={{ 
                            backgroundColor: rowColors?.[index] || '#FFFFFF',
                            borderRadius: '16px',
                            boxShadow: '0 4px 16px -4px rgba(0, 0, 0, 0.15), 0 2px 8px -2px rgba(0, 0, 0, 0.10)',
                            maxWidth: '100%',
                            width: '100%'
                          }}
                          className="border border-gray-200 overflow-hidden active:scale-[0.98] transition-all duration-200"
                        >
                          {/* Card Header - Enhanced visual hierarchy */}
                          <div className="flex items-start justify-between px-4 pt-4 pb-3 border-b border-gray-100">
                            <div className="flex items-start gap-3 flex-1 min-w-0">
                              <div className="flex-shrink-0 mt-0.5">
                                <div 
                                  className="w-12 h-12 rounded-full flex items-center justify-center shadow-sm"
                                  style={{ 
                                    backgroundColor: statusColors?.[statusDisplay] || '#FFD54D',
                                    opacity: 0.9
                                  }}
                                >
                                  {statusDisplay === 'Confirmado' && <CheckCircle className="w-6 h-6 text-white" strokeWidth={2.5} />}
                                  {statusDisplay === 'Agendado' && <Watch className="w-6 h-6 text-white" strokeWidth={2.5} />}
                                  {statusDisplay === 'Em atendimento' && (
                                    <span className="relative flex size-4">
                                      <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-white opacity-75"></span>
                                      <span className="relative inline-flex size-4 rounded-full bg-white"></span>
                                    </span>
                                  )}
                                  {statusDisplay === 'Cancelado' && <XCircle className="w-6 h-6 text-white" strokeWidth={2.5} />}
                                  {statusDisplay === 'Realizado' && <CheckCircle className="w-6 h-6 text-white" strokeWidth={2.5} />}
                                  {statusDisplay === 'Bloqueado' && <Ban className="w-6 h-6 text-white" strokeWidth={2.5} />}
                                  {statusDisplay === 'Novo paciente' && <AlertCircle className="w-6 h-6 text-white" strokeWidth={2.5} />}
                                  {!['Confirmado', 'Agendado', 'Em atendimento', 'Cancelado', 'Realizado', 'Bloqueado', 'Novo paciente'].includes(statusDisplay) && (
                                    <Clock className="w-6 h-6 text-white" strokeWidth={2.5} />
                                  )}
                                </div>
                              </div>
                              <div className="flex-1 min-w-0">
                                <h3 className="text-lg font-bold text-gray-900 truncate leading-tight">
                                  {getPatientName(appointment)}
                                </h3>
                                <p className="text-xs text-gray-500 mt-1.5 font-medium">
                                  Duração: {durationMin} minutos
                                </p>
                              </div>
                            </div>
                          </div>

                          {/* Card Body - Optimized spacing and layout */}
                          <div className="px-4 py-4 space-y-3.5">
                            {/* Time Display - Enhanced visual prominence */}
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-3">
                                <div className="flex-shrink-0">
                                  <div 
                                    className="w-10 h-10 rounded-xl flex items-center justify-center shadow-sm"
                                    style={{ backgroundColor: 'rgba(99, 102, 241, 0.1)' }}
                                  >
                                    <Clock className="w-5 h-5 text-indigo-600" strokeWidth={2.5} />
                                  </div>
                                </div>
                                <div>
                                  <p className="text-xs text-gray-500 font-semibold uppercase tracking-wider">Horário</p>
                                  <p className="text-xl font-bold text-gray-900 mt-0.5">{timeDisplay}</p>
                                </div>
                              </div>
                              
                              {/* Time Until Badge - Enhanced styling */}
                              {timeUntil && (
                                <div className="flex-shrink-0">
                                  <div 
                                    className="px-3 py-2 rounded-xl shadow-sm"
                                    style={{ backgroundColor: 'rgba(99, 102, 241, 0.1)' }}
                                  >
                                    <p className="text-xs font-bold text-indigo-700">
                                      {timeUntil}
                                    </p>
                                  </div>
                                </div>
                              )}
                            </div>

                            {/* Status Display - Enhanced interactive design */}
                            <div 
                              onClick={statusDisplay !== 'Livre' && statusDisplay !== 'Bloqueado' ? (e) => { e?.stopPropagation(); handleStatusClick(e, appointment); } : undefined}
                              className={`flex items-center justify-between px-4 py-3 rounded-xl shadow-sm ${
                                statusDisplay !== 'Livre' && statusDisplay !== 'Bloqueado' ? 'cursor-pointer active:opacity-80 active:scale-[0.98]' : ''
                              } transition-all duration-200`}
                              style={{
                                backgroundColor: statusColors?.[statusDisplay] || '#FFD54D'
                              }}
                            >
                              <div className="flex items-center gap-2.5">
                                {statusDisplay === 'Confirmado' && <CheckCircle className="w-5 h-5 text-white" strokeWidth={2.5} />}
                                {statusDisplay === 'Agendado' && <Watch className="w-5 h-5 text-white" strokeWidth={2.5} />}
                                {statusDisplay === 'Em atendimento' && (
                                  <span className="relative flex size-4">
                                    <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-white opacity-75"></span>
                                    <span className="relative inline-flex size-4 rounded-full bg-white"></span>
                                  </span>
                                )}
                                {statusDisplay === 'Cancelado' && <XCircle className="w-5 h-5 text-white" strokeWidth={2.5} />}
                                {statusDisplay === 'Realizado' && <CheckCircle className="w-5 h-5 text-white" strokeWidth={2.5} />}
                                {statusDisplay === 'Bloqueado' && <Ban className="w-5 h-5 text-white" strokeWidth={2.5} />}
                                {statusDisplay === 'Novo paciente' && <AlertCircle className="w-5 h-5 text-white" strokeWidth={2.5} />}
                                <span className="text-sm font-bold text-white">
                                  {statusDisplay}
                                </span>
                              </div>
                              {statusDisplay !== 'Livre' && statusDisplay !== 'Bloqueado' && (
                                <div className="flex-shrink-0">
                                  <div 
                                    className="w-7 h-7 rounded-full flex items-center justify-center"
                                    style={{ backgroundColor: 'rgba(255, 255, 255, 0.3)' }}
                                  >
                                    <ChevronRight className="w-4 h-4 text-white" strokeWidth={2.5} />
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>

                          {/* Card Footer - Enhanced button styling */}
                          {statusDisplay !== 'Bloqueado' && (
                            <div className="px-4 pb-4 pt-2">
                              <div className="flex items-center gap-2.5">
                                <button
                                  onClick={(e) => handleEditClick(e, appointment)}
                                  className="flex-1 flex items-center justify-center gap-2 py-3 px-4 text-sm font-bold text-indigo-700 rounded-xl transition-all duration-200 active:scale-95 shadow-sm"
                                  style={{ 
                                    backgroundColor: 'rgba(99, 102, 241, 0.1)',
                                    border: '1px solid rgba(99, 102, 241, 0.2)'
                                  }}
                                >
                                  <Pencil className="w-4 h-4" strokeWidth={2.5} />
                                  <span>Editar</span>
                                </button>
                                <button
                                  onClick={(e) => handleDeleteClick(e, appointment?.id)}
                                  className="flex-1 flex items-center justify-center gap-2 py-3 px-4 text-sm font-bold text-red-700 rounded-xl transition-all duration-200 active:scale-95 shadow-sm"
                                  style={{ 
                                    backgroundColor: 'rgba(239, 68, 68, 0.1)',
                                    border: '1px solid rgba(239, 68, 68, 0.2)'
                                  }}
                                >
                                  <Trash2 className="w-4 h-4" strokeWidth={2.5} />
                                  <span>Excluir</span>
                                </button>
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    } else {
                      // Free slot card - Enhanced design
                      const slot = item?.data;
                      return (
                        <div
                          key={item?.id}
                          style={{ 
                            backgroundColor: rowColors?.[index] || '#FFFFFF',
                            borderRadius: '16px',
                            boxShadow: '0 4px 16px -4px rgba(0, 0, 0, 0.15), 0 2px 8px -2px rgba(0, 0, 0, 0.10)',
                            maxWidth: '100%',
                            width: '100%'
                          }}
                          className="border border-gray-200 overflow-hidden"
                        >
                          {/* Free Slot Header - Enhanced visual design */}
                          <div className="flex items-center justify-between px-4 pt-4 pb-3 border-b border-gray-100">
                            <div className="flex items-center gap-3 flex-1">
                              <div className="flex-shrink-0">
                                <div 
                                  className="w-12 h-12 rounded-full flex items-center justify-center shadow-sm"
                                  style={{ 
                                    backgroundColor: 'rgba(102, 207, 131, 0.9)'
                                  }}
                                >
                                  <Calendar className="w-6 h-6 text-white" strokeWidth={2.5} />
                                </div>
                              </div>
                              <div>
                                <h3 className="text-lg font-bold text-gray-900 leading-tight">
                                  Horário Disponível
                                </h3>
                                <p className="text-xs text-gray-500 mt-1.5 font-medium">
                                  Nenhum agendamento
                                </p>
                              </div>
                            </div>
                          </div>

                          {/* Free Slot Body - Enhanced spacing */}
                          <div className="px-4 py-4">
                            <div className="flex items-center justify-between mb-3.5">
                              <div className="flex items-center gap-3">
                                <div className="flex-shrink-0">
                                  <div 
                                    className="w-10 h-10 rounded-xl flex items-center justify-center shadow-sm"
                                    style={{ backgroundColor: 'rgba(16, 185, 129, 0.1)' }}
                                  >
                                    <Clock className="w-5 h-5 text-green-600" strokeWidth={2.5} />
                                  </div>
                                </div>
                                <div>
                                  <p className="text-xs text-gray-500 font-semibold uppercase tracking-wider">Horário</p>
                                  <p className="text-xl font-bold text-gray-900 mt-0.5">{slot?.time}</p>
                                </div>
                              </div>
                            </div>

                            {/* Free Status Badge - Enhanced styling */}
                            <div 
                              className="text-sm font-bold text-white flex items-left justify-left px-4 py-3 rounded-xl shadow-sm"
                              style={{ backgroundColor: 'rgba(102, 207, 131, 0.9)' }}
                            >
                                <ArrowRightFromLine className="w-5 h-5" strokeWidth={2.5} />
                                <span>&nbsp;Livre</span>
                              
                            </div>
                          </div>

                          {/* Free Slot Action - Enhanced button design */}
                          <div className="px-4 pb-4 pt-2">
                            <button
                              onClick={() => handleFreeSlotClick(slot)}
                              className="w-full flex items-center justify-center gap-2.5 py-3.5 px-4 text-sm font-bold text-white rounded-xl transition-all duration-200 active:scale-95 shadow-md"
                              style={{ 
                                backgroundColor: '#6366F1',
                                border: '1px solid rgba(99, 102, 241, 0.3)'
                              }}
                            >
                              <CalendarPlus className="w-5 h-5" strokeWidth={2.5} />
                              <span>Agendar Consulta</span>
                            </button>
                          </div>

                        </div>
                      );
                    }
                  })}
                </div>
              </>
            )}          
        </div>
      </main>
      {/* FIXED: Floating Action Button (FAB) for "Nova Consulta" - Responsive positioning */}
      <button
        onClick={() => {
          setSelectedFreeSlot({ date: new Date(), time: null });
          setShowAppointmentModal(true);
        }}
        className="fixed bottom-6 right-6 lg:top-6 lg:bottom-auto z-50 flex items-center justify-center gap-2 bg-blue-600 hover:bg-indigo-700 text-white rounded-full shadow-lg hover:shadow-xl transition-all duration-200 active:scale-95 p-4 lg:px-6 lg:py-4"
        title="Nova Consulta"
        aria-label="Criar nova consulta"
      >
        <Plus className="w-6 h-6" />
        <span className="hidden lg:inline-block font-medium">Nova Consulta</span>
      </button>


      {/* Modals */}
      {showAppointmentModal && (
        <AppointmentModal
          isOpen={showAppointmentModal}
          patients={patients}
          patientsLoading={patientsLoading}
          selectedDate={selectedFreeSlot?.date || new Date()}
          selectedTime={selectedFreeSlot?.time || null}
          editingAppointment={null}
          preSelectedPatientId={null}
          psychologistProfile={psychologistProfile}
          onClose={handleCloseModal}
          onSave={handleAddAppointment}
        />
      )}
      {selectedAppointment && (
        <AppointmentModal
          isOpen={!!selectedAppointment}
          appointment={selectedAppointment}
          patients={patients}
          patientsLoading={patientsLoading}
          selectedDate={new Date()}
          selectedTime={null}
          editingAppointment={selectedAppointment}
          preSelectedPatientId={null}
          psychologistProfile={psychologistProfile}
          onClose={handleCloseModal}
          onSave={(updates) => handleUpdateAppointment(selectedAppointment?.id, updates)}
          onDelete={() => {
            const appointment = selectedAppointment;
            setSelectedAppointment(null);
            setAppointmentToDelete(appointment);
            setShowDeleteModal(true);
          }}
        />
      )}
      {showDeleteModal && appointmentToDelete && (
        <DeleteAppointmentModal
          isOpen={showDeleteModal}
          onClose={() => {
            setShowDeleteModal(false);
            setAppointmentToDelete(null);
          }}
          onConfirm={() => handleDeleteAppointment(appointmentToDelete?.id)}
          appointmentInfo={{
            patientName: getPatientName(appointmentToDelete),
            date: formatDateDisplay(appointmentToDelete?.appointment_date, appointmentToDelete?.start_time),
            time: appointmentToDelete?.start_time
          }}
          isLoading={isDeletingAppointment}
        />
      )}
      {/* Status Change Modal */}
      {showStatusChangeModal && statusChangeAppointment && (
        <StatusChangeModal
          isOpen={showStatusChangeModal}
          onClose={() => {
            setShowStatusChangeModal(false);
            setStatusChangeAppointment(null);
          }}
          onSave={handleStatusChange}
          appointment={statusChangeAppointment}
          currentStatus={statusChangeAppointment?.status}
        />
      )}
      {/* Success Dialog */}
      <SuccessDialog
        isOpen={showSuccessDialog}
        onClose={() => setShowSuccessDialog(false)}
        message="Consulta agendada com sucesso"
      />
      {/* Edit Success Dialog */}
      <SuccessDialog
        isOpen={showEditSuccessDialog}
        onClose={() => setShowEditSuccessDialog(false)}
        message="Consulta alterada com sucesso"
      />
      {/* Delete Success Dialog */}
      <SuccessDialog
        isOpen={showDeleteSuccessDialog}
        onClose={() => setShowDeleteSuccessDialog(false)}
        message="Consulta excluída com sucesso"
      />
      {/* Dispatch Reminders Success Dialog */}
      <SuccessDialog
        isOpen={showDispatchRemindersSuccessDialog}
        onClose={() => setShowDispatchRemindersSuccessDialog(false)}
        message="Lembretes enviados com sucesso"
      />
      <ErrorDialog
        isOpen={showDispatchRemindersErrorDialog}
        onClose={() => setShowDispatchRemindersErrorDialog(false)}
        message="Falha ao enviar os lembretes. Tente mais tarde."
      />      
      {/* Block Slots Modal */}
      {showBlockSlotsModal && (
        <BlockSlotsModal
          key={currentDate}
          currentDate={currentDate}
          psychologistProfile={psychologistProfile}
          isOpen={showBlockSlotsModal}
          onClose={() => setShowBlockSlotsModal(false)}
          onSuccess={handleSucessBlockSlotModal}
        />
      )}
      {/* New Patient Modal */}
      {showNewPatientModal && (
      <PatientFormModal
        tempPatient={true}
        patient={patient}
        isOpen={showNewPatientModal}
        onClose={() => setShowNewPatientModal(false)}
        onSave={handleAddPatient}
      />      
      )}
      {/* Dispatch Reminders Modal */}
      {showDispatchRemindersModal && (
      <DispatchRemindersModal
        isOpen={showDispatchRemindersModal}
        onSuccess={() => (setShowDispatchRemindersModal(false), setShowDispatchRemindersSuccessDialog(true))}
        onError={() => (setShowDispatchRemindersModal(false), setShowDispatchRemindersErrorDialog(true))}
        onClose={() => setShowDispatchRemindersModal(false)}
        psychologistProfile={psychologistProfile}
      />
      )}      
    </div>
  );
}